import { Platform } from 'react-native';

export type MobileOS = 'ios' | 'android' | null;

const DISMISS_STORAGE_KEY = 'smartAppBanner_dismissedAt';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export function getMobileOS(): MobileOS {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
    return null;
  }

  const ua = navigator.userAgent || '';

  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }

  if (/Android/i.test(ua)) {
    return 'android';
  }

  return null;
}

export function isApplePlatform(): boolean {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';

  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (/Macintosh|MacIntel|MacPPC|Mac68K|Mac OS X/.test(ua)) return true;
  if (/Mac/.test(platform)) return true;
  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;

  return false;
}

/**
 * Detects if current web browser uses Apple's WebKit engine and requires FairPlay DRM.
 * Returns true for: Safari on macOS, all browsers on iOS/iPadOS (they all use WebKit).
 * Returns false for: Chrome/Edge/Firefox on macOS (they use Blink/Gecko and support Widevine).
 */
export function isAppleBrowser(): boolean {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';

  // All browsers on iOS/iPadOS use WebKit
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;

  // Safari on macOS: has Apple vendor, no Chrome/Chromium/Firefox/OPR in UA
  const isAppleVendor = (navigator.vendor || '').includes('Apple');
  const isNotChromium = !/CriOS|Chrome|Chromium|OPR|Edg|Firefox|FxiOS/.test(ua);

  return isAppleVendor && isNotChromium;
}

export function isInstagramInAppBrowser(): boolean {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  return /Instagram/i.test(ua);
}

export function isIOSInstagramBrowser(): boolean {
  return getMobileOS() === 'ios' && isInstagramInAppBrowser();
}

export function isBannerDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false;

  try {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!dismissedAt) return false;

    const timestamp = parseInt(dismissedAt, 10);
    if (isNaN(timestamp)) return false;

    return Date.now() - timestamp < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

export function dismissBanner(): void {
  if (typeof localStorage === 'undefined') return;

  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
  } catch {}
}
